import React, {useContext, useMemo, useState} from "react"
import {Tooltip} from "antd"
import cx from "classnames"
import {useTranslation} from "react-i18next"
import {omit} from "lodash"
import {Auth} from "types/auth"
import {FinAidOffer, FinAidOfferStatus, FinAidPackage, FinAidWorksheetStatus, HousingPlan} from "types/fin-aid/fin-aid"
import {DEPENDENCY_STATUS_OPTIONS} from "types/students"
import {ActiveStudentContext} from "context/ActiveStudentContext"
import {finAidService, userService} from "services"
import {handleError} from "helpers"
import {useAllPayingMethods} from "hooks"
import {KlassDropdown} from "components/Select"
import {BaseInput} from "components/inputs"
import {Icon} from "components/Icon"
import CircularSpin from "components/CircularSpin"
import styles from "uiKit/StudentInfoPanel/StudentInfoPanel.module.css"

type Field =
    | "dependencyStatus"
    | "numberOfDependents"
    | "payingMethods"
    | "housingPlan"
    | "awardYear"
    | "packageStatus"
    | "isir"

type Props = {
    field: Field
    student: Auth.DepartmentStudent
    readOnly?: boolean
    fallbackDependencyStatus?: string
    finAidOffer?: FinAidOffer
    onOfferUpdated?: (offer: FinAidOffer) => void
}

const FIELD_LABEL: Record<Field, string> = {
    dependencyStatus: "Dependency Status",
    numberOfDependents: "Number of Dependents",
    payingMethods: "Method of paying for College",
    housingPlan: "Housing Plan",
    awardYear: "Current Award Year",
    packageStatus: "Financial Aid Package Status",
    isir: "Current ISIR"
}

const OFFER_FIELDS: Field[] = ["awardYear", "packageStatus", "isir"]

const currentYear = new Date().getFullYear()
const AWARD_YEAR_OPTIONS = [-1, 0, 1, 2, 3, 4, 5, 6].map((index) => ({
    id: currentYear + index,
    name: `${currentYear + index} - ${currentYear + index + 1}`
}))

const dropdownComponents = {
    ClearIndicator: () => <div className="w-20" />,
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null
}

const PACKAGE_RELATIONS_REQUIRING_ID = ["subLoanInfo", "unsubLoanInfo", "plusLoanInfo", "summary"] as const

/**
 * Worksheet create omits `finAidPackageId` and still requires it on nested loan,
 * summary, and mid-year transfer rows. Offer edit rejects `midYearTransfer: null`
 * and null summary rate lists, which the loaded package returns from the database.
 */
function buildFinAidPackagePayload({
    source,
    student,
    awardYear,
    isir,
    includePackageId
}: {
    source?: FinAidPackage
    student: Auth.DepartmentStudent
    awardYear: number
    isir: string | null
    includePackageId: boolean
}) {
    const pkg: Record<string, any> = source
        ? omit(source, ["createdAt", "updatedAt", "deletedAt", ...(includePackageId ? [] : ["finAidPackageId"])])
        : {
              ssn: student.ssn || "",
              gradeLevel: 1,
              numberOfTerms: 0,
              loanYear: awardYear
          }

    pkg.studentProfileId = student.profileId
    pkg.firstName = student.firstName || ""
    pkg.lastName = student.lastName || ""
    pkg.awardYear = awardYear
    pkg.isir = isir

    for (const key of PACKAGE_RELATIONS_REQUIRING_ID) {
        if (pkg[key] && pkg[key].finAidPackageId == null) {
            delete pkg[key]
        }
    }

    if (pkg.midYearTransfer == null) {
        delete pkg.midYearTransfer
    }

    if (pkg.summary) {
        pkg.summary = {
            ...pkg.summary,
            cashPaymentTermRates: pkg.summary.cashPaymentTermRates ?? [],
            federalWorkStudyTermRates: pkg.summary.federalWorkStudyTermRates ?? []
        }
    }

    if (Array.isArray(pkg.midYearTransferAmounts)) {
        const amounts = pkg.midYearTransferAmounts.filter(
            (item) => item?.finAidPackageId != null && item?.order != null
        )
        if (amounts.length) {
            pkg.midYearTransferAmounts = amounts
        } else {
            delete pkg.midYearTransferAmounts
        }
    }

    if (Array.isArray(pkg.aidSubsequences)) {
        pkg.aidSubsequences = pkg.aidSubsequences.map((item) => omit(item, ["secondDisbursementDates"]))
    }

    return pkg
}

const dropdownStyleCustom = {
    menuPortal: (menuStyles) => ({
        ...menuStyles,
        zIndex: 9999
    }),
    menu: (menuStyles) => ({
        ...menuStyles,
        width: "max-content",
        minWidth: "100%",
        maxWidth: "450px",
        right: 0
    })
}

export function EditableFinAidField({
    field,
    student,
    readOnly = false,
    fallbackDependencyStatus,
    finAidOffer,
    onOfferUpdated
}: Props) {
    const {t} = useTranslation(["financialAid"])
    const {payingMethods} = useAllPayingMethods()
    const {setStudent, reloadStudent} = useContext(ActiveStudentContext)
    const [isEditing, setEditing] = useState(false)
    const [isSubmitting, setSubmitting] = useState(false)
    const [value, setValue] = useState<any>()
    const editable = !readOnly
    const dependencyStatus = student.dependencyStatus || fallbackDependencyStatus

    React.useEffect(() => {
        setEditing(false)
    }, [student.profileId, finAidOffer?.finAidOfferId])

    const housingPlanOptions = useMemo(
        () =>
            Object.values(HousingPlan).map((housingPlan) => ({
                id: housingPlan,
                name: t(`financialAid:housingPlan.${housingPlan}`)
            })),
        [t]
    )

    const awardYearOptions = useMemo(() => {
        const options = [...AWARD_YEAR_OPTIONS]
        if (finAidOffer?.awardYear && !options.some((option) => option.id === finAidOffer.awardYear)) {
            options.unshift({
                id: finAidOffer.awardYear,
                name: `${finAidOffer.awardYear} - ${finAidOffer.awardYear + 1}`
            })
        }
        return options
    }, [finAidOffer?.awardYear])

    const packageStatusOptions = useMemo(
        () =>
            Object.values(FinAidOfferStatus).map((status) => ({
                id: status,
                name: t(`financialAid:finAidOffer.status.${status}`)
            })),
        [t]
    )

    const dependencyStatusOptions = useMemo(() => {
        const options = [...DEPENDENCY_STATUS_OPTIONS]
        if (dependencyStatus && !options.some((option) => option.id === dependencyStatus)) {
            options.push({
                id: dependencyStatus as Auth.DepartmentStudent["dependencyStatus"],
                name: t(`financialAid:financialAidPackage.dependencyStatuses.${dependencyStatus}`)
            })
        }
        return options
    }, [dependencyStatus, t])

    const displayValue = useMemo(() => {
        switch (field) {
            case "dependencyStatus":
                return dependencyStatus
                    ? t(`financialAid:financialAidPackage.dependencyStatuses.${dependencyStatus}`)
                    : "-"
            case "numberOfDependents":
                return student.numberOfDependents === null || student.numberOfDependents === undefined
                    ? "-"
                    : String(student.numberOfDependents)
            case "payingMethods":
                return student.payingMethods?.map((method) => method.name).join(", ") || "-"
            case "housingPlan":
                return student.housingPlan ? t(`financialAid:housingPlan.${student.housingPlan}`) : "-"
            case "awardYear":
                return finAidOffer?.awardYear ? `${finAidOffer.awardYear} - ${finAidOffer.awardYear + 1}` : "-"
            case "packageStatus":
                return finAidOffer?.status ? t(`financialAid:finAidOffer.status.${finAidOffer.status}`) : "-"
            case "isir":
                return finAidOffer?.finAidPackage?.isir || "-"
            default:
                return "-"
        }
    }, [
        dependencyStatus,
        field,
        finAidOffer?.awardYear,
        finAidOffer?.finAidPackage?.isir,
        finAidOffer?.status,
        student.housingPlan,
        student.numberOfDependents,
        student.payingMethods,
        t
    ])

    const getInitialValue = () => {
        switch (field) {
            case "dependencyStatus":
                return dependencyStatusOptions.find((option) => option.id === dependencyStatus)
            case "numberOfDependents":
                return student.numberOfDependents ?? ""
            case "payingMethods":
                return student.payingMethods ?? []
            case "housingPlan":
                return housingPlanOptions.find((option) => option.id === student.housingPlan)
            case "awardYear":
                return awardYearOptions.find((option) => option.id === finAidOffer?.awardYear)
            case "packageStatus":
                return packageStatusOptions.find((option) => option.id === finAidOffer?.status)
            case "isir":
                return finAidOffer?.finAidPackage?.isir || ""
            default:
                return undefined
        }
    }

    const handleEditClick = () => {
        if (!editable) return
        setValue(getInitialValue())
        setEditing(true)
    }

    const renderInput = () => {
        switch (field) {
            case "dependencyStatus":
                return (
                    <KlassDropdown
                        isClearable
                        options={dependencyStatusOptions}
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "numberOfDependents":
                return (
                    <BaseInput
                        disabledArrow
                        type="number"
                        min={0}
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                    />
                )
            case "payingMethods":
                return (
                    <KlassDropdown
                        isMulti
                        isClearable
                        options={payingMethods}
                        valueKey="payingMethodId"
                        labelKey="name"
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "housingPlan":
                return (
                    <KlassDropdown
                        isClearable
                        options={housingPlanOptions}
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "awardYear":
                return (
                    <KlassDropdown
                        options={awardYearOptions}
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "packageStatus":
                return (
                    <KlassDropdown
                        options={packageStatusOptions}
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "isir":
                return <BaseInput value={value} onChange={setValue} className={styles.contentInput} />
            default:
                return null
        }
    }

    const saveOfferField = async () => {
        const awardYear = field === "awardYear" ? value?.id : finAidOffer?.awardYear
        const status = field === "packageStatus" ? value?.id : finAidOffer?.status
        const isir = field === "isir" ? value || null : finAidOffer?.finAidPackage?.isir ?? null

        if (field === "awardYear" && !awardYear) return
        if (field === "packageStatus" && !status) return

        if (finAidOffer?.finAidOfferId) {
            if (field === "isir") {
                const finAidPackage = buildFinAidPackagePayload({
                    source: finAidOffer.finAidPackage,
                    student,
                    awardYear: awardYear ?? new Date().getFullYear(),
                    isir,
                    includePackageId: true
                })
                await finAidService.updateFinAidOffer({
                    finAidOfferId: finAidOffer.finAidOfferId,
                    finAidPackageId: finAidOffer.finAidPackageId,
                    finAidPackage
                })
                onOfferUpdated?.({
                    ...finAidOffer,
                    finAidPackage: {...finAidOffer.finAidPackage, ...finAidPackage, isir}
                })
            } else if (field === "awardYear") {
                await finAidService.updateFinAidOffer({
                    finAidOfferId: finAidOffer.finAidOfferId,
                    awardYear
                })
                onOfferUpdated?.({...finAidOffer, awardYear})
            } else if (field === "packageStatus") {
                await finAidService.updateFinAidOffer({
                    finAidOfferId: finAidOffer.finAidOfferId,
                    status
                })
                onOfferUpdated?.({...finAidOffer, status})
            }
            setEditing(false)
            return
        }

        if (!student.profileId) return
        const year = awardYear ?? new Date().getFullYear()
        const worksheetStatus =
            status === FinAidOfferStatus.Certified
                ? FinAidWorksheetStatus.Certified
                : status === FinAidOfferStatus.Packaged
                ? FinAidWorksheetStatus.Packaged
                : FinAidWorksheetStatus.Saved
        const finAidPackage = buildFinAidPackagePayload({
            source: finAidOffer?.finAidPackage,
            student,
            awardYear: year,
            isir,
            includePackageId: Boolean(finAidOffer?.finAidWorksheetId)
        })

        const saved = finAidOffer?.finAidWorksheetId
            ? await finAidService.updateFinAidWorksheet({
                  finAidWorksheetId: finAidOffer.finAidWorksheetId,
                  finAidPackageId: finAidOffer.finAidPackageId,
                  awardYear: year,
                  status: worksheetStatus,
                  finAidPackage,
                  savePackage: true
              })
            : await finAidService.createFinAidWorksheet({
                  studentProfileId: student.profileId,
                  awardYear: year,
                  status: worksheetStatus,
                  finAidPackage,
                  savePackage: true
              })

        const latest = await finAidService.getFinAidLatestOffer({
            studentProfileId: student.profileId,
            linkedPackage: true
        })
        onOfferUpdated?.({
            ...(finAidOffer || {}),
            ...saved,
            ...(latest?.finAidOfferId ? latest : {}),
            studentProfileId: student.profileId,
            awardYear: latest?.awardYear ?? year,
            status: latest?.status ?? status,
            finAidWorksheetId: latest?.finAidWorksheetId ?? saved?.finAidWorksheetId ?? finAidOffer?.finAidWorksheetId,
            finAidPackageId: latest?.finAidPackageId ?? saved?.finAidPackageId ?? finAidOffer?.finAidPackageId,
            finAidPackage: {
                ...(finAidPackage || {}),
                ...(saved?.finAidPackage || {}),
                ...(latest?.finAidPackage || {}),
                isir
            }
        })
        setEditing(false)
    }

    const handleSaveClick = async () => {
        if (isSubmitting) return
        if (OFFER_FIELDS.includes(field)) {
            try {
                setSubmitting(true)
                await saveOfferField()
            } catch (error) {
                handleError(error)
            } finally {
                setSubmitting(false)
            }
            return
        }
        if (!student.profileId) return
        let submitData: Record<string, unknown> = {}
        let nextStudent: Partial<Auth.DepartmentStudent> = {}

        switch (field) {
            case "dependencyStatus":
                submitData = {dependencyStatus: value?.id ?? null}
                nextStudent = {dependencyStatus: value?.id ?? null}
                break
            case "numberOfDependents": {
                const parsed =
                    value === "" || value === null || value === undefined || Number.isNaN(Number(value))
                        ? null
                        : Number(value)
                submitData = {numberOfDependents: parsed}
                nextStudent = {numberOfDependents: parsed}
                break
            }
            case "payingMethods":
                submitData = {
                    payingMethodIds: (value || []).map((method) => method.payingMethodId)
                }
                nextStudent = {payingMethods: value || []}
                break
            case "housingPlan":
                submitData = {housingPlan: value?.id ?? null}
                nextStudent = {housingPlan: value?.id ?? null}
                break
            default:
                return
        }

        try {
            setSubmitting(true)
            await userService.updateProfileUserById(student.profileId, {
                type: student.type || Auth.UserProfileType.Student,
                ...submitData
            })
            setStudent?.((prev) => (prev ? {...prev, ...nextStudent} : prev))
            setEditing(false)
            if (reloadStudent) {
                reloadStudent(student.profileId).catch(() => undefined)
            }
        } catch (error) {
            handleError(error)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className={styles.contentWrap}>
            <div
                className={cx(styles.contentTitle, {[styles.editable]: editable})}
                onClick={editable ? handleEditClick : undefined}
                title={FIELD_LABEL[field]}>
                {FIELD_LABEL[field]}
            </div>
            {isEditing ? (
                <div className={styles.contentValueWrap}>
                    <div className={styles.contentValue}>{renderInput()}</div>
                    <div className={styles.contentActions}>
                        {isSubmitting ? (
                            <CircularSpin size="small" />
                        ) : (
                            <div className={styles.contentAction} onClick={handleSaveClick}>
                                <Icon icon="CHECK_CIRCLE" />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <Tooltip title={displayValue} placement="topRight">
                    <div
                        className={cx(styles.contentValue, {[styles.editable]: editable})}
                        onClick={editable ? handleEditClick : undefined}>
                        <span className={styles.displayValue}>{displayValue}</span>
                    </div>
                </Tooltip>
            )}
        </div>
    )
}
